import { useState, useEffect, useRef, useCallback } from "react";
import type { PanchangLocation } from "../../lib/panchang";
import { LocationPinIcon } from "./lunarIcons";

const LS_KEY = "vu.panchang.location";
const GEO_ASKED_KEY = "vu.panchang.geoAsked";

/** Reverse-geocode coordinates to a readable "City, Country" label. Free,
 *  keyless, CORS-enabled client endpoint; falls back gracefully. */
async function reverseGeocode(lat: number, lng: number): Promise<string> {
  try {
    const r = await fetch(
      `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lng}&localityLanguage=en`,
    );
    if (!r.ok) return "Your location";
    const d: Record<string, unknown> = await r.json();
    const city = (d.city || d.locality || d.principalSubdivision || "") as string;
    const country = (d.countryName || "") as string;
    // Keep it short — city + country (the saṅkalpa names the place succinctly).
    const label = [city, country].filter(Boolean).join(", ");
    return label || "Your location";
  } catch {
    return "Your location";
  }
}

function loadFromStorage(): PanchangLocation | null {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (
      parsed !== null &&
      typeof parsed === "object" &&
      "lat" in parsed && typeof (parsed as Record<string, unknown>).lat === "number" &&
      "lng" in parsed && typeof (parsed as Record<string, unknown>).lng === "number" &&
      "tz" in parsed && typeof (parsed as Record<string, unknown>).tz === "string" &&
      "place" in parsed && typeof (parsed as Record<string, unknown>).place === "string"
    ) {
      const p = parsed as Record<string, unknown>;
      return {
        lat: p.lat as number,
        lng: p.lng as number,
        tz: p.tz as string,
        place: p.place as string,
      };
    }
  } catch {
    // ignore
  }
  return null;
}

function saveToStorage(loc: PanchangLocation | null): void {
  try {
    if (loc === null) {
      localStorage.removeItem(LS_KEY);
    } else {
      localStorage.setItem(LS_KEY, JSON.stringify(loc));
    }
  } catch {
    // ignore
  }
}

export function useLocationPref(): {
  location: PanchangLocation | null;
  setLocation: (loc: PanchangLocation | null) => void;
} {
  const [location, setLocationState] = useState<PanchangLocation | null>(() =>
    loadFromStorage(),
  );

  const setLocation = useCallback((loc: PanchangLocation | null) => {
    saveToStorage(loc);
    setLocationState(loc);
  }, []);

  // On first visit (no saved preference yet), ask the browser for the user's
  // location by default. If granted we use it; if denied/unavailable we fall
  // back to the server default (Krnjak). We record that we've asked so we don't
  // re-prompt on every load. Runs once.
  useEffect(() => {
    if (location) return; // already have a saved preference
    let asked = false;
    try {
      asked = localStorage.getItem(GEO_ASKED_KEY) === "1";
    } catch {
      /* ignore */
    }
    if (asked || !navigator.geolocation) return;
    try {
      localStorage.setItem(GEO_ASKED_KEY, "1");
    } catch {
      /* ignore */
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = Math.round(pos.coords.latitude * 10000) / 10000;
        const lng = Math.round(pos.coords.longitude * 10000) / 10000;
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        void reverseGeocode(lat, lng).then((place) => setLocation({ lat, lng, tz, place }));
      },
      () => {
        /* denied / unavailable → keep null → server uses Krnjak fallback */
      },
      { timeout: 10000 },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { location, setLocation };
}

interface Props {
  location: PanchangLocation | null;
  onLocationChange: (loc: PanchangLocation | null) => void;
}

export function LocationPicker({ location, onLocationChange }: Props) {
  const [open, setOpen] = useState(false);
  const [geoLoading, setGeoLoading] = useState(false);
  const [geoError, setGeoError] = useState<string | null>(null);
  const [manualLat, setManualLat] = useState("");
  const [manualLng, setManualLng] = useState("");
  const [manualPlace, setManualPlace] = useState("");
  const [manualError, setManualError] = useState<string | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const placeName = location?.place ?? "Krnjak";

  const handleGeoLocate = () => {
    if (!navigator.geolocation) {
      setGeoError("Geolocation is not supported by your browser.");
      return;
    }
    setGeoLoading(true);
    setGeoError(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const lat = Math.round(pos.coords.latitude * 10000) / 10000;
        const lng = Math.round(pos.coords.longitude * 10000) / 10000;
        void reverseGeocode(lat, lng).then((place) => {
          onLocationChange({ lat, lng, tz, place });
          setGeoLoading(false);
          setOpen(false);
        });
      },
      () => {
        setGeoError("Location access denied or unavailable.");
        setGeoLoading(false);
      },
      { timeout: 10000 },
    );
  };

  const handleManualSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setManualError(null);
    const lat = parseFloat(manualLat);
    const lng = parseFloat(manualLng);
    const place = manualPlace.trim();
    if (Number.isNaN(lat) || lat < -90 || lat > 90) {
      setManualError("Latitude must be between −90 and 90.");
      return;
    }
    if (Number.isNaN(lng) || lng < -180 || lng > 180) {
      setManualError("Longitude must be between −180 and 180.");
      return;
    }
    if (!place) {
      setManualError("Please enter a place name.");
      return;
    }
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    onLocationChange({ lat, lng, tz, place });
    setManualLat("");
    setManualLng("");
    setManualPlace("");
    setOpen(false);
  };

  const handleReset = () => {
    onLocationChange(null);
    setOpen(false);
  };

  return (
    <div className="pa-location-wrap" ref={wrapRef}>
      <button
        type="button"
        className={`pa-location-btn${open ? " is-open" : ""}`}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="true"
      >
        <span>Timings for {placeName}</span>
        <span className="pa-location-btn-chevron" aria-hidden>▾</span>
      </button>

      {open && (
        <div className="pa-location-popover" role="dialog" aria-label="Choose location for panchāṅga timings">
          <p className="pa-location-popover-title">Location for timings</p>

          <button
            type="button"
            className="pa-location-geo-btn"
            onClick={handleGeoLocate}
            disabled={geoLoading}
          >
            <LocationPinIcon size={14} className="pa-location-geo-icon" />
            {geoLoading ? "Detecting…" : "Use my location"}
          </button>
          {geoError && <p className="pa-location-error">{geoError}</p>}

          <div className="pa-location-divider">or enter manually</div>

          <form className="pa-location-form" onSubmit={handleManualSubmit}>
            <div className="pa-location-row">
              <input
                className="pa-location-input"
                type="text"
                inputMode="decimal"
                placeholder="Latitude"
                value={manualLat}
                onChange={(e) => setManualLat(e.target.value)}
                aria-label="Latitude"
              />
              <input
                className="pa-location-input"
                type="text"
                inputMode="decimal"
                placeholder="Longitude"
                value={manualLng}
                onChange={(e) => setManualLng(e.target.value)}
                aria-label="Longitude"
              />
            </div>
            <input
              className="pa-location-input"
              type="text"
              placeholder="Place name"
              value={manualPlace}
              onChange={(e) => setManualPlace(e.target.value)}
              aria-label="Place name"
            />
            {manualError && <p className="pa-location-error">{manualError}</p>}
            <div className="pa-location-actions">
              <button type="submit" className="btn-app">
                Set location
              </button>
              {location && (
                <button
                  type="button"
                  className="pa-location-reset"
                  onClick={handleReset}
                >
                  Reset to default
                </button>
              )}
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
